/**
 * GONG Training — comportements des sections `training-center-*`.
 *
 * Aucune dépendance externe. Tout est progressif : sans JS, la piste du lieu
 * reste scrollable au doigt, les tarifs restent visibles et le sélecteur de
 * sport reste un lien — aucun contenu n'est inaccessible.
 *
 * 1. Mesure la hauteur réelle de l'en-tête de page et l'expose en variable CSS,
 *    pour que le hero tienne dans le viewport utile (CTA visibles sans scroll).
 * 2. Barre CTA collante : masquée tant que les boutons du hero sont à l'écran
 *    — mêmes actions — et en bas de page pour ne pas couvrir le footer.
 * 3. Bandeau « Le lieu » : piste défilante au doigt, avance automatique,
 *    pastilles, pause au survol, après un geste et hors onglet actif.
 * 4. Réservation : le hero et la barre CTA rejouent le clic du bouton que
 *    l'app de réservation injecte dans la carte de séance. Le bouton « Carnet »
 *    vise la carte qui s'achète.
 *
 * Une page par sport depuis le 25/08/2026 : le sélecteur du hero ne sélectionne
 * rien, son premier segment dit où l'on est et les suivants sont des liens.
 * Rien à piloter ici — c'est du balisage.
 */
(function () {
  'use strict';

  var root = document.documentElement;

  /**
   * Sept sections rendent `training-center-assets`, donc sept balises
   * `<script>` pointant ici. Le navigateur ne télécharge le fichier qu'une
   * fois, mais il **exécute chaque balise** : sans ce garde-fou, tout ce qui
   * s'écoute sur `document` est posé sept fois.
   *
   * Longtemps invisible, parce que nos gestionnaires sont idempotents — poser
   * sept fois le même écouteur de défilement ne se voit pas. Le défaut s'est
   * révélé le 21/08/2026 sur un gestionnaire qui écrivait **hors** de la page
   * (un envoi produisait sept requêtes) ; ce gestionnaire-là est parti depuis,
   * le garde-fou reste. **S'en souvenir avant d'en ajouter un du même genre.**
   */
  if (root.dataset.trainingCenterBooted) return;
  root.dataset.trainingCenterBooted = 'true';

  function debounce(fn, wait) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------------------------------------------------------------- Header */

  /**
   * Hauteur cumulée de tout ce qui précède le contenu de page (barre
   * d'annonce + header + navigation). On mesure la position du conteneur
   * principal plutôt qu'un id de section group : le thème n'enveloppe pas ses
   * sections d'en-tête dans un wrapper dédié.
   */
  function measureHeader() {
    var main = document.getElementById('MainContent');
    if (!main) return;

    var offset = Math.round(main.getBoundingClientRect().top + window.pageYOffset);
    if (offset > 0 && offset < window.innerHeight) {
      root.style.setProperty('--training-center-header-offset', offset + 'px');
    }
  }

  /* ---------------------------------------------------------- Barre collante */

  /**
   * La barre est masquée pour deux raisons, réévaluées au même endroit :
   * - les boutons du hero sont à l'écran (elle ferait doublon, et sur mobile
   *   elle recouvrirait le bandeau d'avantages en pied de hero) ;
   * - on est arrivé en bas de page (elle recouvrirait le footer).
   *
   * On relit le hero à chaque passage plutôt que de l'observer une fois pour
   * toutes : l'éditeur de thème remplace la section à chaud, et un
   * IntersectionObserver braqué sur un nœud détaché ne se réveille plus —
   * la barre restait alors masquée pour toujours.
   */
  function initStickyBar() {
    var bar = document.querySelector('.training-center-sticky');
    if (!bar || bar.dataset.trainingCenterStickyReady === 'true') return;
    bar.dataset.trainingCenterStickyReady = 'true';

    var spacer = document.querySelector('.training-center-sticky-spacer');

    function measureBar() {
      var height = Math.round(bar.getBoundingClientRect().height);
      if (height > 0) {
        root.style.setProperty('--training-center-sticky-height', height + 'px');
      }
    }

    function sync() {
      var hidden = false;

      // Tant que les boutons du hero sont à l'écran, la barre ferait doublon :
      // même sélecteur, mêmes actions. Elle apparaît dès qu'ils sortent.
      var actions = document.querySelector(
        '.training-center-hero__actions:not([hidden])'
      );
      if (actions) {
        var box = actions.getBoundingClientRect();
        hidden = box.bottom > 0 && box.top < window.innerHeight;
      }

      if (!hidden) {
        var scrolled = window.pageYOffset + window.innerHeight;
        hidden = scrolled >= document.documentElement.scrollHeight - 240;
      }

      bar.classList.toggle('is-hidden', hidden);
      bar.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      if (spacer) spacer.style.visibility = hidden ? 'hidden' : '';
    }

    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener(
      'resize',
      debounce(function () {
        measureBar();
        sync();
      }, 150)
    );

    measureBar();
    sync();
  }

  /* --------------------------------------------------- Bandeau « Le lieu » */

  /**
   * Le bandeau est une piste défilante, comme les sujets des panneaux de
   * sport : le doigt emmène l'image avec lui, le navigateur recale sur la
   * diapositive la plus proche au relâchement. Le JS ne fait qu'ajouter
   * l'avance automatique, les pastilles et le clavier.
   *
   * Le fondu enchaîné précédent ne bougeait qu'au relâchement, une fois un
   * seuil de 45 px franchi : le geste ne donnait aucune prise et le bandeau
   * passait pour figé.
   */
  function initBanner(banner) {
    if (banner.dataset.trainingCenterBannerReady === 'true') return;

    // Un bandeau sans piste n'a rien à armer. Le hero en portait un du temps
    // où ses diapositives se croisaient au changement de sport ; il ne se
    // déclare plus bandeau du tout depuis qu'il n'en rend qu'une.
    var track = banner.querySelector('[data-banner-track]');
    if (!track) return;

    var slides = Array.prototype.slice.call(
      banner.querySelectorAll('[data-banner-slide]')
    );
    if (slides.length < 2) return;

    // Le drapeau se pose une fois les conditions réunies, pas avant : sinon
    // une section à qui l'éditeur ajoute une deuxième image reste inerte.
    banner.dataset.trainingCenterBannerReady = 'true';

    var dots = Array.prototype.slice.call(
      banner.querySelectorAll('[data-banner-dot]')
    );
    var interval = parseInt(banner.dataset.bannerInterval, 10) || 7000;
    var index = 0;
    var timer = null;
    var resume = null;

    // `is-active` ne sert plus à montrer la diapositive — c'est le défilement
    // qui s'en charge — mais à savoir laquelle porte le travelling avant.
    function render() {
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === index);
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    function goTo(next) {
      index = (next + slides.length) % slides.length;

      track.scrollTo({
        left: slides[index].offsetLeft - track.offsetLeft,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });

      render();
    }

    // Le défilement fait autorité : on recale l'index sur la diapositive la
    // plus proche, qu'il vienne du doigt ou de nous.
    function syncFromScroll() {
      var closest = 0;
      var min = Infinity;
      slides.forEach(function (slide, i) {
        var distance = Math.abs(
          slide.offsetLeft - track.offsetLeft - track.scrollLeft
        );
        if (distance < min) {
          min = distance;
          closest = i;
        }
      });
      if (closest !== index) {
        index = closest;
        render();
      }
    }

    // Défilement automatique coupé si l'utilisateur limite les animations :
    // la piste et les pastilles restent le moyen de tout voir.
    function play() {
      if (timer || prefersReducedMotion() || !banner.isConnected) return;
      timer = setInterval(function () {
        // L'éditeur de thème remplace la section à chaud : le bandeau d'origine
        // est détaché mais son minuteur, lui, continuait de tourner sur un
        // nœud invisible. Il se coupe tout seul au premier tour qui suit.
        if (!banner.isConnected) return pause();
        goTo(index + 1);
      }, interval);
    }

    function pause() {
      clearInterval(timer);
      timer = null;
      clearTimeout(resume);
      resume = null;
    }

    // Après un geste, on laisse la main au visiteur un tour complet avant de
    // reprendre : reprendre aussitôt lui arracherait l'image qu'il vient de
    // choisir.
    function pauseThenPlay() {
      pause();
      resume = setTimeout(play, interval);
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        goTo(i);
        pauseThenPlay();
      });
    });

    track.addEventListener('pointerdown', pause);
    track.addEventListener('pointerup', pauseThenPlay);
    track.addEventListener('pointercancel', pauseThenPlay);
    track.addEventListener('scroll', debounce(syncFromScroll, 90), { passive: true });

    track.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(index - 1);
        pauseThenPlay();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(index + 1);
        pauseThenPlay();
      }
    });

    banner.addEventListener('mouseenter', pause);
    banner.addEventListener('mouseleave', play);
    banner.addEventListener('focusin', pause);
    banner.addEventListener('focusout', play);

    // Onglet en arrière-plan : inutile de continuer à tourner. L'écouteur est
    // sur `document`, donc partagé : on l'enregistre par bandeau, mais il ne
    // survit pas au détachement grâce au garde de `play`.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) pause();
      else play();
    });

    render();
    // Onglet déjà en arrière-plan au chargement : on ne lance rien. Chrome n'y
    // anime pas les défilements, les pastilles auraient avancé sans l'image.
    if (!document.hidden) play();
  }

  function initBanners(scope) {
    (scope || document)
      .querySelectorAll('[data-training-center-banner]')
      .forEach(initBanner);
  }

  /* --------------------------------------- Relais vers le bouton de l'offre */

  /**
   * Un seul bouton réserve vraiment : celui de la carte d'offre, qui ajoute le
   * variant de séance au panier. L'app de réservation reconnaît ce variant,
   * intercepte l'envoi et ouvre son calendrier sur le bon événement — c'est le
   * variant qui désigne l'événement, pas un identifiant saisi quelque part.
   *
   * Le hero et la barre CTA ne refont pas ce chemin : ils rejouent le clic sur
   * ce bouton-là, désigné par son `id`. Rien à recopier d'un réglage à l'autre,
   * et le `href` du lien reste un repli utile — sans JavaScript, ou sans
   * contexte de réservation, il fait défiler jusqu'à l'offre.
   *
   * On visait auparavant le bouton de l'app par une classe CSS recopiée à
   * l'identique des deux côtés, et on n'osait le cliquer qu'après avoir vu
   * `data-servicify-modal-bound` — un pari sur le balisage interne d'un tiers.
   * Ici les deux bouts sont à nous.
   */

  /**
   * Ce que fait réellement Easy Appointment Booking, vérifié sur la page le
   * 20/08/2026 : elle **n'intercepte pas** l'envoi du formulaire. Elle repère
   * nos formulaires d'ajout au panier (elle y pose `data-servicify-form`),
   * **masque notre bouton** (`display: none`) et **injecte le sien** juste à
   * côté — `.servicify-select-a-time-button`, libellé « Sélectionnez une
   * heure », auquel elle recopie nos classes pour qu'il ait notre allure.
   *
   * Deux conséquences, toutes deux contre-intuitives :
   *
   * - c'est le bouton de l'app que le visiteur clique sur une carte d'offre,
   *   jamais le nôtre. Le nôtre reste dans le DOM, invisible ;
   * - **cliquer le nôtre court-circuite le calendrier** : le formulaire part
   *   vraiment vers /cart/add et vend une séance sans créneau. C'est arrivé au
   *   premier essai du relais.
   *
   * Le relais vise donc le bouton de l'app, jamais le nôtre. Et sa présence
   * dans le pied de carte est à elle seule le garde-fou : elle prouve que
   * l'app a bien accroché ce formulaire-là. Pas de bouton d'app, pas de
   * relais — le `href` fait défiler jusqu'à l'offre, et on ne vend rien à
   * l'aveugle.
   *
   * Un `SERVICIFY_PRODUCT` nul ne veut rien dire ici : il l'est avec un bloc
   * d'app non paramétré, c'est-à-dire dans la configuration que la page
   * utilise. Ne pas s'en servir comme signal.
   */
  /**
   * Retrouver le bouton de l'app dans un pied de carte.
   *
   * Aucun `id` n'entre en jeu : l'app n'en pose pas sur son bouton. On dépend
   * donc de noms qui lui appartiennent, et qu'une mise à jour peut changer —
   * d'où trois prises successives, de la plus précise à la plus générale. Il
   * faudrait que les trois cèdent en même temps pour perdre le relais, et même
   * alors la panne reste bénigne : pas de bouton trouvé, pas de relais, le
   * `href` fait défiler jusqu'à l'offre.
   *
   * La troisième prise ne dépend d'aucun nom : dans le pied d'une carte, il
   * n'y a que notre bouton — que l'app masque quand elle prend la main — et
   * celui qu'elle injecte. Un bouton visible qui n'est pas le nôtre ne peut
   * être que le sien.
   *
   * Et si l'app est absente, cette troisième prise ne trouve rien : notre
   * bouton est alors le seul, et il est exclu. C'est ce qui garantit qu'on ne
   * vend jamais une séance sans créneau.
   */
  function isVisible(el) {
    return el.getClientRects().length > 0;
  }

  function appBookButton(scope, ours) {
    var found =
      scope.querySelector('.servicify-select-a-time-button') ||
      scope.querySelector('button[class*="servicify"]') ||
      scope.querySelector('[class*="servicify"] button');

    if (!found) {
      var buttons = Array.prototype.slice.call(scope.querySelectorAll('button'));
      for (var i = 0; i < buttons.length; i++) {
        if (buttons[i] !== ours && isVisible(buttons[i])) {
          found = buttons[i];
          break;
        }
      }
    }

    // Garde-fou ultime : notre bouton envoie le formulaire pour de vrai.
    return found === ours ? null : found;
  }

  function initBookingRelay() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-training-center-relay]');
      if (!trigger) return;

      // Notre bouton ne sert que de repère : il désigne la carte, il n'est
      // jamais cliqué.
      var anchor = document.getElementById(trigger.dataset.trainingCenterRelay);
      if (!anchor) {
        console.warn(
          '[training-center] Aucune carte nommée « ' +
            trigger.dataset.trainingCenterRelay +
            ' » : le lien fait défiler vers l\'offre.'
        );
        return;
      }

      var scope = anchor.closest('.training-center-card__footer') || anchor.parentElement;
      var appButton = appBookButton(scope, anchor);

      if (!appButton) {
        console.warn(
          "[training-center] L'app de réservation n'a pas posé son bouton sur " +
            "cette carte — bloc d'app absent, app embed désactivée, ou balisage " +
            "changé. On fait défiler vers l'offre plutôt que d'ajouter au " +
            "panier sans créneau."
        );
        return;
      }

      event.preventDefault();
      appButton.click();
    });
  }

  /* ------------------------------------ Descente vers la carte de carnet */

  /**
   * Le bouton « Carnet » descend vers la carte de carnet, pas vers le haut du
   * bloc d'offre. Sur mobile les cartes sont empilées : l'ancre de section
   * déposait le visiteur sur la première carte — la séance — et le carnet
   * restait sous la ligne de flottaison. Sur desktop les trois cartes sont
   * côte à côte, le défaut ne se voyait pas.
   *
   * On ne repère pas le carnet par son rang : un rang se décale au premier
   * réagencement de cartes, et c'est le travers qu'on a retiré partout
   * ailleurs. On le repère par ce qui le définit — **c'est la carte qui
   * s'achète**. L'app de réservation prend la main sur les cartes de séance et
   * masque notre bouton ; celle qui garde le nôtre visible est le carnet.
   *
   * On **corrige le `href`** au lieu d'intercepter le clic, et c'est
   * délibéré : sur ce thème `html` a une hauteur figée et `body` porte
   * l'`overflow`, si bien qu'aucun défilement programmatique n'aboutit — ni
   * `scrollIntoView`, ni `location.hash`, ni `window.scrollTo`. Le clic natif
   * sur une ancre, lui, trouve le bon conteneur tout seul. On le laisse donc
   * faire, en lui donnant simplement la bonne cible.
   *
   * L'app injecte ses boutons de façon asynchrone — au premier passage, nos
   * boutons sont tous encore visibles et le carnet se confondrait avec la
   * séance. On ne devine donc pas un délai : on **observe** le bloc d'offre et
   * on recalcule dès que l'app y touche.
   *
   * Rien trouvé : on ne touche à rien et le `href` d'origine — le bloc
   * d'offre — s'applique, comme avant.
   */
  function purchaseCard(offer) {
    var cards = Array.prototype.slice.call(offer.querySelectorAll('.training-center-plan'));

    for (var i = 0; i < cards.length; i++) {
      var ours = cards[i].querySelector('button[type="submit"]');
      /**
       * On teste le `display` propre au bouton, pas sa visibilité à l'écran :
       * le panneau du sport inactif est `hidden`, donc *tous* ses boutons sont
       * hors écran, y compris celui du carnet. Le lien du Yoga ne se serait
       * jamais résolu tant que l'onglet Training était affiché.
       */
      if (ours && getComputedStyle(ours).display !== 'none') return cards[i];
    }

    return null;
  }

  function refreshPassLinks() {
    document.querySelectorAll('[data-training-center-pass]').forEach(function (link) {
      var offer = document.getElementById(link.dataset.trainingCenterPass);
      if (!offer) return;

      var card = purchaseCard(offer);
      if (card && card.id) link.setAttribute('href', '#' + card.id);
    });
  }

  function initPassLinks() {
    refreshPassLinks();

    var watched = [];
    var observer = new MutationObserver(debounce(refreshPassLinks, 100));

    document.querySelectorAll('[data-training-center-pass]').forEach(function (link) {
      var offer = document.getElementById(link.dataset.trainingCenterPass);
      if (!offer || watched.indexOf(offer) !== -1) return;

      watched.push(offer);
      // Les liens observés vivent hors du bloc d'offre : les réécrire ne
      // relance pas l'observateur.
      observer.observe(offer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden']
      });
    });
  }

  /* ------------------------------------------------------------------ Boot */

  function init() {
    measureHeader();
    initBookingRelay();
    initPassLinks();
    initStickyBar();
    initBanners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', measureHeader);
  window.addEventListener('resize', debounce(measureHeader, 150));

  // Le theme editor recharge les sections à chaud.
  document.addEventListener('shopify:section:load', function (event) {
    measureHeader();
    initBanners(event.target);
    initStickyBar();
  });
  document.addEventListener('shopify:section:unload', measureHeader);
})();
